import {useMemo, useState, useCallback} from "react";

/**
 * useRefundSettlement
 *
 * 管理退費結算邏輯的自訂 Hook。
 *
 * 功能：
 * - 管理「現金退費」與「信用卡退費」的金額輸入
 * - 即時計算「實際退費總額」
 * - 驗證退費金額是否合法（不得為負數，必須等於應退總計，子表無錯誤）
 * - 將錯誤訊息收斂為 `errors` 陣列，供畫面提示或送出前檢查
 *
 * @param {Object} opts - 設定參數
 * @param {number} opts.expectedTotal - 應退總計（由父層彙總子表回傳的 total）
 * @param {boolean} [opts.tablesHasError=false] - 是否有來自子表的輸入錯誤
 *
 * @returns {Object} 狀態與操作方法
 * @returns {number} return.cashAmount - 當前輸入的現金退費金額
 * @returns {number} return.cardAmount - 當前輸入的信用卡退費金額
 * @returns {number} return.actualRefund - 現金 + 信用卡的實際退費總額
 * @returns {string[]} return.errors - 當前驗證錯誤訊息清單
 * @returns {Function} return.setCash - 設定現金退費金額 (setCash(value: number|string))
 * @returns {Function} return.setCard - 設定信用卡退費金額 (setCard(value: number|string))
 * @returns {Function} return.isError - 是否有錯誤 (回傳 boolean)
 *
 * @example
 * const {
 *   cashAmount,
 *   cardAmount,
 *   actualRefund,
 *   errors,
 *   setCash,
 *   setCard,
 *   isError,
 * } = useRefundSettlement({ expectedTotal: 500, tablesHasError: false });
 *
 * setCash(200);
 * setCard(300);
 *
 * console.log(actualRefund); // 500
 * console.log(errors); // [] (若合法)
 * console.log(isError()); // false
 */

const useRefundSettlement = ({expectedTotal = 0, tablesHasError = false} = {}) => {
    const [cashAmount, setCashAmount] = useState(0);
    const [cardAmount, setCardAmount] = useState(0);

    const actualRefund = useMemo(() => {
            const c = Number(cashAmount) || 0;
            const k = Number(cardAmount) || 0;
            return c + k;
        },
        // eslint-disable-next-line
        [cashAmount, cardAmount]);

    const setCash = useCallback((v) => setCashAmount(Number(v) || 0),
        // eslint-disable-next-line
        []);
    const setCard = useCallback((v) => setCardAmount(Number(v) || 0),
        // eslint-disable-next-line
        []);

    const errors = useMemo(() => {
            const list = [];
            if (cashAmount < 0) list.push("現金不可為負數");
            if (cardAmount < 0) list.push("信用卡不可為負數");
            if (actualRefund !== expectedTotal) list.push("實際退費金額需等於應退費總計");
            if (tablesHasError) list.push("部分折讓輸入不合法，請修正後再送出");
            return list;
        },
        // eslint-disable-next-line
        [cashAmount, cardAmount, actualRefund, expectedTotal, tablesHasError]);

    const isError = useCallback(() => errors.length > 0,
        // eslint-disable-next-line
        [errors]);

    return {
        cashAmount,
        cardAmount,
        actualRefund,
        errors,
        setCash,
        setCard,
        isError,
    };
};

export default useRefundSettlement;
